// Plan/feature helpers driven by /api/auth/me. The server is authoritative —
// these only decide what the UI shows proactively (gates, upgrade CTAs).
import type { BillingCycle, Me, PlanId, SiteCard, SiteRole } from "../api/types";

export const PLAN_ORDER: PlanId[] = ["free", "basic", "pro", "fleet"];

/** Display names (the catalog's `name`), for gates that only know an id. */
export const PLAN_NAMES: Record<PlanId, string> = {
  free: "Free Cloud",
  basic: "Basic",
  pro: "Pro",
  fleet: "Fleet",
};

/** A plan id (or anything the server sent as `required_plan`) → its name.
 *  Unknown ids pass through so a newer server plan still reads sensibly. */
export function planName(id: string | null | undefined): string {
  if (!id) return "";
  return PLAN_NAMES[id as PlanId] ?? id;
}

export function hasFeature(me: Me | null, feature: string): boolean {
  return !!me && me.features.includes(feature);
}

export function planRank(plan: PlanId): number {
  return PLAN_ORDER.indexOf(plan);
}

/** What choosing `planId` on `cycle` means relative to the current
 *  subscription. Mirrors the server's quote (routes/subscription._quote):
 *  Free is cycle-less, so Free on either cycle IS the current plan when the
 *  account is on Free — "switching" it to annual is not a change at all. */
export function planRelation(
  planId: PlanId,
  cycle: BillingCycle,
  currentId: PlanId,
  currentCycle: BillingCycle,
): "current" | "upgrade" | "downgrade" {
  if (planId === currentId) {
    if (currentId === "free" || cycle === currentCycle) return "current";
    return cycle === "annual" ? "upgrade" : "downgrade";
  }
  return planRank(planId) > planRank(currentId) ? "upgrade" : "downgrade";
}

/** Role ladder, same ranks as the server's orgs_repo.ROLE_RANK. */
const ROLE_RANK: Record<SiteRole, number> = {
  guest: 0,
  viewer: 1,
  operator: 2,
  admin: 3,
  owner: 4,
};

export function roleAtLeast(role: string | null | undefined, minimum: SiteRole): boolean {
  if (!role) return false;
  const rank = ROLE_RANK[role as SiteRole];
  return rank !== undefined && rank >= ROLE_RANK[minimum];
}

/** Writes on a site (ack an alert, queue/cancel a command, restore a
 *  backup, edit the site) need operator or higher — the server's
 *  `_require_write_role`. */
export function canWriteSite(site: Pick<SiteCard, "is_owner" | "role"> | undefined): boolean {
  if (!site) return false;
  return site.is_owner || roleAtLeast(site.role, "operator");
}

/** The features that gate a SITE's tabs: the site OWNER's plan, which is what
 *  the server checks (`_require_site_feature`). Falls back to the viewer's
 *  own features only when the viewer IS the owner; otherwise returns null —
 *  "unknown", so the UI renders the tab and lets the server's answer (an
 *  upgrade_required 403 rendered as a gate) decide, instead of walling a
 *  member off with the wrong account's plan. */
export function siteFeatures(
  site: Pick<SiteCard, "is_owner" | "owner_features"> | undefined,
  me: Pick<Me, "features"> | null,
): string[] | null {
  if (site?.owner_features) return site.owner_features;
  if (site?.is_owner && me) return me.features;
  return null;
}

/** Share management: the owner, or an org admin (server: require_share_admin).
 *  Older servers send no `can_share`; fall back to ownership. */
export function canShareSite(site: Pick<SiteCard, "is_owner" | "can_share"> | undefined): boolean {
  if (!site) return false;
  return site.can_share ?? site.is_owner;
}

/** Claim codes / re-linking hardware: the owner or an org operator+ — never a
 *  direct share. Older servers send no `can_claim`; fall back to ownership. */
export function canClaimSite(site: Pick<SiteCard, "is_owner" | "can_claim"> | undefined): boolean {
  if (!site) return false;
  return site.can_claim ?? site.is_owner;
}

/** Feature keys → i18n label keys for the pricing/feature tables. */
export const FEATURE_LABEL_KEYS: Record<string, string> = {
  monitor: "features.monitor",
  settings_backup: "features.settings_backup",
  device_health: "features.device_health",
  remote_admin: "features.remote_admin",
  data_backup: "features.data_backup",
  alerts: "features.alerts",
  export: "features.export",
  share_site: "features.share_site",
  entsoe_prices: "features.entsoe_prices",
  optimization_insights: "features.optimization_insights",
  pv_forecast: "features.pv_forecast",
  planned_vs_actual: "features.planned_vs_actual",
  incident_reports: "features.incident_reports",
  daily_analysis: "features.daily_analysis",
  monthly_savings: "features.monthly_savings",
  suggestions: "features.suggestions",
  fleet_dashboard: "features.fleet_dashboard",
  roles: "features.roles",
  remote_diagnostics: "features.remote_diagnostics",
  fleet_reporting: "features.fleet_reporting",
  api_access: "features.api_access",
  fleet_optimization: "features.fleet_optimization",
  config_templates: "features.config_templates",
  white_label: "features.white_label",
};
